/** Fiche client — miroir API (goutte clients v2). */
import { PCG_CUSTOMER_DEFAULT } from "../../pcg/pcg";

export type SireneStatus = "active" | "establishment_closed" | "ceased" | "unknown";

export type ClientContact = {
  id?: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  add_to_invoices: boolean;
};

export type ClientPay = {
  method: "sepa" | "transfer" | string;
  label: string;
  bank_last4?: string;
  status?: string;
};

export type Client = {
  id: number;
  company_id: number;
  name: string;
  display_name?: string | null;
  pay?: ClientPay;
  siret: string | null;
  siren: string | null;
  vat_number: string | null;
  website: string | null;
  phone: string | null;
  email: string | null;
  routing_id: string | null;
  vat_valid: number;
  tax_exempt: number;
  address_line: string | null;
  address_line2: string | null;
  postal_code: string | null;
  city: string | null;
  region: string | null;
  country_code: string;
  ship_address_line: string | null;
  ship_address_line2: string | null;
  ship_postal_code: string | null;
  ship_city: string | null;
  ship_region: string | null;
  ship_country_code: string | null;
  created_at?: string | null;
  archived?: number;
  sirene_status?: SireneStatus | null;
  sirene_checked_at?: string | null;
  sirene_closed_on?: string | null;
  sirene_created_on?: string | null;
  contacts?: ClientContact[];
  pcg_code?: string | null;
  pcg_sub?: string | null;
};

export type ClientListItem = {
  id: number;
  company_id: number;
  name: string;
  display_name?: string | null;
  email: string | null;
  phone?: string | null;
  contact_name?: string | null;
  contact_names?: string | null;
  siret: string | null;
  created_at: string;
  portal_last_seen_at?: string | null;
  archived: number;
  sirene_status?: SireneStatus | null;
  sirene_checked_at?: string | null;
  sirene_closed_on?: string | null;
  sirene_created_on?: string | null;
  paid_to_date_cents: number;
  balance_cents: number;
};

export type StatementLine = {
  id: number;
  doc_type: "invoice" | "credit_note";
  number: string | null;
  issue_date: string;
  due_date: string | null;
  total_ttc_cents: number;
  paid_cents: number;
  balance_cents: number;
};

export type StatementPayment = {
  id: number;
  document_id: number;
  document_number: string | null;
  paid_at: string;
  amount_cents: number;
  refunded_cents?: number;
  method: string;
};

export type ClientStatement = {
  client: { id: number; name: string; siret: string | null; email: string | null; address_line: string | null; postal_code: string | null; city: string | null };
  period?: { from: string | null; to: string | null };
  opening_balance_cents?: number;
  lines: StatementLine[];
  payments: StatementPayment[];
  totals: { invoiced_cents: number; credit_cents: number; paid_to_date_cents: number; balance_cents: number };
};

export type ClientKind = "particulier" | "micro" | "societe";

export function inferClientKind(c: {
  tax_exempt?: boolean | number | null;
  siret?: string | null;
  siren?: string | null;
  vat_number?: string | null;
}): ClientKind {
  if (Boolean(c.tax_exempt)) return "micro";
  const siret = String(c.siret || "").replace(/\D/g, "");
  const siren = String(c.siren || "").replace(/\D/g, "");
  if (siret.length >= 9 || siren.length === 9 || String(c.vat_number || "").trim() !== "") return "societe";
  return "particulier";
}

export type ClientDraft = {
  id?: number;
  kind: ClientKind;
  name: string;
  display_name: string;
  siret: string;
  siren: string;
  vat_number: string;
  website: string;
  phone: string;
  email: string;
  routing_id: string;
  vat_valid: boolean;
  tax_exempt: boolean;
  address_line: string;
  address_line2: string;
  postal_code: string;
  city: string;
  region: string;
  country_code: string;
  ship_address_line: string;
  ship_address_line2: string;
  ship_postal_code: string;
  ship_city: string;
  ship_region: string;
  ship_country_code: string;
  contacts: ClientContact[];
  pcg_code: string;
  pcg_sub: string;
  sirene_status?: SireneStatus;
  sirene_closed_on?: string;
  sirene_created_on?: string;
};

export const COUNTRIES: { code: string; label: string }[] = [
  { code: "FR", label: "France" },
  { code: "BE", label: "Belgique" },
  { code: "CH", label: "Suisse" },
  { code: "LU", label: "Luxembourg" },
  { code: "DE", label: "Allemagne" },
  { code: "ES", label: "Espagne" },
  { code: "IT", label: "Italie" },
  { code: "GB", label: "Royaume-Uni" },
];

export function emptyDraft(): ClientDraft {
  return {
    kind: "societe",
    name: "",
    display_name: "",
    siret: "",
    siren: "",
    vat_number: "",
    website: "",
    phone: "",
    email: "",
    routing_id: "",
    vat_valid: false,
    tax_exempt: false,
    address_line: "",
    address_line2: "",
    postal_code: "",
    city: "",
    region: "",
    country_code: "FR",
    ship_address_line: "",
    ship_address_line2: "",
    ship_postal_code: "",
    ship_city: "",
    ship_region: "",
    ship_country_code: "",
    contacts: [],
    pcg_code: PCG_CUSTOMER_DEFAULT,
    pcg_sub: "",
  };
}

export function clientToDraft(c: Client): ClientDraft {
  const d = emptyDraft();
  d.id = c.id;
  d.name = c.name ?? "";
  d.display_name = c.display_name ?? "";
  d.siret = c.siret ?? "";
  d.siren = c.siren ?? "";
  d.vat_number = c.vat_number ?? "";
  d.website = c.website ?? "";
  d.phone = c.phone ?? "";
  d.email = c.email ?? "";
  d.routing_id = c.routing_id ?? "";
  d.vat_valid = Boolean(c.vat_valid);
  d.tax_exempt = Boolean(c.tax_exempt);
  d.address_line = c.address_line ?? "";
  d.address_line2 = c.address_line2 ?? "";
  d.postal_code = c.postal_code ?? "";
  d.city = c.city ?? "";
  d.region = c.region ?? "";
  d.country_code = c.country_code || "FR";
  d.ship_address_line = c.ship_address_line ?? "";
  d.ship_address_line2 = c.ship_address_line2 ?? "";
  d.ship_postal_code = c.ship_postal_code ?? "";
  d.ship_city = c.ship_city ?? "";
  d.ship_region = c.ship_region ?? "";
  d.ship_country_code = c.ship_country_code ?? "";
  d.kind = inferClientKind(c);
  d.pcg_code = c.pcg_code || PCG_CUSTOMER_DEFAULT;
  d.pcg_sub = c.pcg_sub || "";
  d.contacts = (c.contacts ?? []).map((x) => ({
    first_name: x.first_name ?? "",
    last_name: x.last_name ?? "",
    email: x.email ?? "",
    phone: x.phone ?? "",
    add_to_invoices: Boolean(x.add_to_invoices),
  }));
  return d;
}

export function emptyContact(): ClientContact {
  return { first_name: "", last_name: "", email: "", phone: "", add_to_invoices: true };
}
